import React, { useState } from "react";

//#region 型定義
/** ファイルドロップエリアのプロパティ型 */
interface FileDropAreaProps {
  /** ファイルドロップ時のコールバック */
  onDrop: (files: FileList) => void;
  /** エラーメッセージ */
  error?: string | null;
  /** 受け入れ可能なファイル形式 */
  accept?: string;
  /** カスタムメッセージ */
  customMessage?: {
    title?: string;
    description?: string;
    fileTypes?: string;
  };
}
//#endregion

/**
 * ファイルドロップエリアコンポーネント
 * @description ドラッグ&ドロップでのファイル選択を提供する
 */
export const FileDropArea: React.FC<FileDropAreaProps> = ({
  onDrop,
  error,
  accept = ".nc",
  customMessage = {
    title: "NCファイルを選択",
    description:
      "ファイルをドラッグ＆ドロップするか、クリックして選択してください",
    fileTypes: "NCファイル (.nc) のみ",
  },
}) => {
  //#region ステート定義
  /** ドラッグ状態を管理 */
  const [isDragging, setIsDragging] = useState(false);
  //#endregion

  //#region ドラッグ操作イベント処理
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  //#endregion

  //#region ファイル処理
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      onDrop(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onDrop(e.target.files);
    }
  };
  //#endregion

  //#region レンダリング
  return (
    <div
      className={`
        relative flex justify-center px-4 py-4 sm:px-6 sm:pt-5 sm:pb-6
        border-2 border-dashed rounded-md transition-colors duration-200
        ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}
        ${error ? "border-red-500" : ""}
      `}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="space-y-1 text-center">
        <svg
          className={`mx-auto h-12 w-12 ${isDragging ? "text-blue-500" : "text-gray-400"}`}
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
          aria-hidden="true"
        >
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className="flex text-sm text-gray-600 justify-center">
          <label
            htmlFor="file-input"
            className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
          >
            <span>ファイルを選択</span>
            <input
              id="file-input"
              type="file"
              accept={accept}
              className="sr-only"
              onChange={handleFileSelect}
            />
          </label>
          <p className="pl-1">またはドラッグ＆ドロップ</p>
        </div>

        <p className="text-xs text-gray-500">{customMessage.fileTypes}</p>

        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      </div>
    </div>
  );
  //#endregion
};

export default FileDropArea;